import { between, multiply } from '@ipfabric/utils/fp/number';
import type { GraphRendererPlugin } from 'components/Graph/GraphApp/plugins/GraphRendererPlugin';
import { option } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import type { ClampZoomOptions, Plugin as ViewportPlugin } from 'pixi-viewport';

/**
 * Internal type of pixi-viewport not exposed in its public typing
 */
interface ClampZoom extends ViewportPlugin {
  clamp: () => void;
  options: ClampZoomOptions;
}

const MIN_SCALE_FACTOR = 0.6; // min scale = scale when graph fits to viewport * MIN_SCALE_FACTOR
const MIN_MIN_SCALE = 0.1;
const MAX_MIN_SCALE = 2;
const MAX_SCALE = 10;

function getMinScale(elementSize: number, areaSize: number): number {
  return pipe(
    areaSize / elementSize,
    multiply(MIN_SCALE_FACTOR),
    between(MIN_MIN_SCALE, MAX_MIN_SCALE),
  );
}

export const clampZoom: GraphRendererPlugin = (pluginInterface) => {
  pluginInterface.viewport.clampZoom();
  const clampZoomPlugin = pluginInterface.viewport.plugins.get(
    'clamp-zoom',
  ) as ClampZoom;

  clampZoomPlugin.options = {
    maxScale: MAX_SCALE,
    minScale: MIN_MIN_SCALE,
  };

  let requestedMinScale = MIN_MIN_SCALE;

  /**
   * When min scale is changed so that current scale doesn't fit it,
   * restrict only zooming out further and retry applying min scale the same way
   * any time zoom changes.
   */
  const softClampMinScale = () => {
    clampZoomPlugin.options.minScale = Math.min(
      requestedMinScale,
      // Viewport 'zoomed' event is fired before clamp plugin is applied,
      // so we need to apply last minScale by ourselves here
      Math.max(
        clampZoomPlugin.options.minScale!,
        pluginInterface.viewport.scale.x,
      ),
    );
  };

  pluginInterface.viewport.on('zoomed', () => {
    softClampMinScale();
  });

  pluginInterface.events.addListener('graphBoundsChange', ({ newBounds }) => {
    pipe(
      newBounds,
      option.map((bounds) => {
        requestedMinScale = Math.min(
          getMinScale(
            bounds.right - bounds.left,
            pluginInterface.viewport.screenWidth,
          ),
          getMinScale(
            bounds.bottom - bounds.top,
            pluginInterface.viewport.screenHeight,
          ),
        );
        softClampMinScale();
        clampZoomPlugin.clamp();
      }),
    );
  });
};
